import React, { useCallback, useEffect, useMemo } from "react"
import { useRouter } from "next/router"

import { LogInRequired } from "@/app-shell/layouts/LogInRequired"
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Card,
} from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { EmptyStateCTA } from "@/components/EmptyStateCTA"
import { NewResourceButton } from "@/components/ResourceActionButton"
import { usePageHeaderController } from "@/contexts/PageHeaderContext"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { useQueryParams } from "@/hooks/useQueryParams"

import {
  CalendarDateRangeButton,
  DashboardActions,
  EmptyDashboardState,
} from "."
import { useDashboardDatePreferences } from "../hooks/useDashboardDatePreferences"
import { useDashboardTableCellDrillDown } from "../hooks/useDashboardTableCellDrillDown"
import { useUnifiedMetricDrillDownInsights } from "../hooks/useUnifiedMetricDrillDownInsights"
import { useUnifiedMetricDrillDownNavigation } from "../hooks/useUnifiedMetricDrillDownNavigation"
import { DashboardDrilldownInsightsPanel } from "../llm-generation"
import { getDrillDownBreadcrumbLabel } from "../utils/drillDownBreadcrumbs"
import { buildDrillDownConversationPath } from "../utils/drillDownNavigation"
import { GridDashboardLayout } from "../widgets/components/grid/GridDashboardLayout"
import { WidgetFillerContent } from "../widgets/components/WidgetFillerContent"
import {
  DashboardFiltersProvider,
  useDashboardFilters,
} from "../widgets/contexts/DashboardFiltersContext"
import { useDashboardWidgets } from "../widgets/hooks/useDashboardWidgets"
import type { ChartClickData } from "../widgets/types/chart"
import type { BucketClickData, DrillDownRunRecord } from "../widgets/types/core"
import { DEFAULT_DASHBOARD_DATE_PRESET } from "../widgets/types/dashboardConfig"
import {
  buildDashboardWidgetConfigurationHref,
  getDashboardDisplayName,
  resolveDashboardDateRangeOverride,
} from "../widgets/utils/dashboardPageState"
import { DashboardLoadingState } from "./DashboardLoadingState"
import { DashboardTableCellDrillDownDrawer } from "./DashboardTableCellDrillDownDrawer"
import { UnifiedMetricDrillDownPanel } from "./UnifiedMetricDrillDownPanel"

interface DashboardContentProps {
  dashboardId?: string
}

interface DashboardPageProps {
  dashboardId?: string
}

const DashboardContent: React.FC<DashboardContentProps> = ({ dashboardId }) => {
  const router = useRouter()
  const organizationSlug = router.query.slug as string
  const { trackEvent } = useAnalytics()
  const queryParams = useQueryParams()
  const urlStart = queryParams.get("start")
  const urlEnd = queryParams.get("end")
  const currentTab = queryParams.get("tab")

  // Using dashboard filters context
  const { state: filterState, handlerSetGlobalOverride } = useDashboardFilters()

  const {
    dashboards,
    currentDashboardId,
    widgets,
    isSavingDashboard,
    isLoading,
    handleRemoveWidget,
    handleCloneWidget,
    handleUpdateWidget,
    handleClearDashboard,
    handleExportDashboard,
    handleImportDashboard,
    handleDeleteDashboard,
    handleCreateDashboard,
    handleWidgetMove,
    handleWidgetWidthChange,
  } = useDashboardWidgets(dashboardId)

  const { savedPreferences } = useDashboardDatePreferences(currentDashboardId)

  const {
    selection: tableCellDrillDown,
    open: handleTableCellClick,
    close: closeTableCellDrillDown,
  } = useDashboardTableCellDrillDown()

  // Remove modal disclosure since we're using dropdown navigation
  const { setCustomPageHeader } = usePageHeaderController()

  const currentDashboard = dashboards.find((d) => d.id === currentDashboardId)
  const activeDateRange = filterState.isOverrideActive
    ? filterState.globalDateRangeOverride
    : null

  const {
    unifiedMetricDrillDown,
    parentUnifiedMetricDrillDown,
    openUnifiedMetricDrillDown,
    openParentUnifiedMetricDrillDown,
    closeUnifiedMetricDrillDown,
  } = useUnifiedMetricDrillDownNavigation()

  const handleOpenDrillDownConversation = useCallback(
    (row: Pick<DrillDownRunRecord, "run_id" | "simulation_output_id">) => {
      if (!unifiedMetricDrillDown) {
        return
      }

      queryParams.push(
        buildDrillDownConversationPath(
          row,
          unifiedMetricDrillDown.clickData.dataSource,
        ),
        organizationSlug,
      )
    },
    [organizationSlug, queryParams, unifiedMetricDrillDown],
  )

  const handleWidgetPointClick = useCallback(
    (point: BucketClickData, widgetId: string, metricId?: string) => {
      openUnifiedMetricDrillDown(
        point as unknown as ChartClickData,
        widgetId,
        metricId,
      )
    },
    [openUnifiedMetricDrillDown],
  )

  const handleWidgetChartClick = useCallback(
    (point: BucketClickData, widgetId: string, metricId?: string) => {
      const widget = widgets.find((w) => w.id === widgetId)
      if (widget?.type !== "chart") {
        return
      }

      openUnifiedMetricDrillDown(
        point as unknown as ChartClickData,
        widgetId,
        metricId,
      )
    },
    [openUnifiedMetricDrillDown, widgets],
  )

  const clearAllSelections = useCallback(() => {
    closeUnifiedMetricDrillDown()
  }, [closeUnifiedMetricDrillDown])

  const hasInitializedDateRef = React.useRef<string | null>(null)
  const lastDashboardIdRef = React.useRef<string | null>(null)
  const initialDateRangeOverride = useMemo(
    () =>
      resolveDashboardDateRangeOverride({
        defaultPreset: DEFAULT_DASHBOARD_DATE_PRESET,
        savedPreferences,
        urlStart,
        urlEnd,
      }),
    [savedPreferences, urlStart, urlEnd],
  )

  useEffect(() => {
    if (!currentDashboardId) {
      return
    }

    // Reset initialization when switching dashboards
    if (lastDashboardIdRef.current !== currentDashboardId) {
      lastDashboardIdRef.current = currentDashboardId
      hasInitializedDateRef.current = null
    }

    // Only initialize once per dashboard
    if (hasInitializedDateRef.current === currentDashboardId) {
      return
    }

    // Wait for dashboard to load (so we know if there are saved preferences)
    if (isLoading || !currentDashboard) {
      return
    }

    handlerSetGlobalOverride(initialDateRangeOverride)
    hasInitializedDateRef.current = currentDashboardId
  }, [
    currentDashboardId,
    currentDashboard,
    handlerSetGlobalOverride,
    initialDateRangeOverride,
    isLoading,
  ])

  // Track dashboard page loaded
  useEffect(() => {
    if (!isLoading && currentDashboardId) {
      trackEvent(TRACKING_EVENTS.DASHBOARD.PAGE_LOADED, {
        dashboard_id: currentDashboardId,
        dashboard_name: currentDashboard?.display_name || "",
        widget_count: widgets.length,
      })
    }
  }, [
    isLoading,
    currentDashboardId,
    currentDashboard?.display_name,
    widgets.length,
    trackEvent,
  ])

  useEscapeShortcut({
    description: "Back to Dashboard",
    enabled: !!unifiedMetricDrillDown,
    handler: clearAllSelections,
    priority: ESCAPE_SHORTCUT_PRIORITY.PAGE_FALLBACK,
  })

  // Compute focused widget title for breadcrumb
  const focusedWidgetForBreadcrumb = useMemo(() => {
    const widgetId = unifiedMetricDrillDown?.widgetId
    if (!widgetId) {
      return null
    }
    return widgets.find((w) => w.id === widgetId) ?? null
  }, [unifiedMetricDrillDown, widgets])

  const focusedChartTitle =
    focusedWidgetForBreadcrumb?.config?.title ||
    focusedWidgetForBreadcrumb?.title ||
    null

  const dashboardDisplayName = getDashboardDisplayName(currentDashboard)
  const currentDrillDownLabel = useMemo(
    () =>
      unifiedMetricDrillDown
        ? getDrillDownBreadcrumbLabel(unifiedMetricDrillDown.clickData, {
            chartTitle: focusedChartTitle,
          })
        : null,
    [focusedChartTitle, unifiedMetricDrillDown],
  )
  const hasPointDrillDown =
    !!unifiedMetricDrillDown &&
    (unifiedMetricDrillDown.clickData.drillDownScope ?? "point") === "point"
  const handleFocusedChartBreadcrumbClick =
    parentUnifiedMetricDrillDown && hasPointDrillDown
      ? openParentUnifiedMetricDrillDown
      : clearAllSelections
  const {
    dashboardInsightChart,
    dashboardInsightRows,
    focusedInsightSimulationOutputId,
    handleInsightEvidenceLinkClick,
    handleInsightEvidenceLinkHover,
    highlightedInsightSimulationOutputId,
    unifiedDrillDownRowsState,
  } = useUnifiedMetricDrillDownInsights({
    currentDashboardId,
    focusedChartTitle,
    onInsightRowOpen: handleOpenDrillDownConversation,
    unifiedMetricDrillDown,
  })

  // Update breadcrumb to include chart title when in focused mode
  useEffect(() => {
    if (focusedChartTitle && dashboardDisplayName) {
      setCustomPageHeader(
        <div className="flex flex-1 min-w-0 items-center">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <span>Dashboard</span>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbButton onClick={clearAllSelections}>
                  {dashboardDisplayName}
                </BreadcrumbButton>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {hasPointDrillDown ? (
                  <BreadcrumbButton onClick={handleFocusedChartBreadcrumbClick}>
                    {focusedChartTitle}
                  </BreadcrumbButton>
                ) : currentDrillDownLabel ? (
                  <span>{focusedChartTitle}</span>
                ) : (
                  <BreadcrumbPage>{focusedChartTitle}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {currentDrillDownLabel && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{currentDrillDownLabel}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>,
      )
    } else {
      setCustomPageHeader(null)
    }

    return () => {
      setCustomPageHeader(null)
    }
  }, [
    focusedChartTitle,
    dashboardDisplayName,
    currentDrillDownLabel,
    hasPointDrillDown,
    handleFocusedChartBreadcrumbClick,
    setCustomPageHeader,
    clearAllSelections,
  ])

  const getWidgetConfigurationHref = useCallback(
    (widgetId?: string) =>
      queryParams.getHref(
        buildDashboardWidgetConfigurationHref({
          organizationSlug,
          dashboardId: currentDashboardId ?? "",
          ...(widgetId ? { widgetId } : {}),
          currentTab,
          activeDateRange,
        }),
        organizationSlug,
      ),
    [
      activeDateRange,
      currentDashboardId,
      currentTab,
      organizationSlug,
      queryParams,
    ],
  )

  const handleAddWidgetClick = useCallback(() => {
    if (!currentDashboardId) {
      return
    }

    void router.push(getWidgetConfigurationHref())
  }, [currentDashboardId, getWidgetConfigurationHref, router])

  const handleConfigureWidget = useCallback(
    (id: string) => {
      if (!currentDashboardId || !widgets.find((w) => w.id === id)) {
        return
      }

      void router.push(getWidgetConfigurationHref(id))
    },
    [currentDashboardId, getWidgetConfigurationHref, router, widgets],
  )

  // While the dashboard and its widget list load, the page shows its own chrome
  // and nothing else. The widgets themselves are the only placeholder: each card
  // arrives at its final size on the grid and fills in as its data lands, so a
  // stand-in layout here would only be a second arrangement to replace.
  if (isLoading) {
    return <DashboardLoadingState />
  }

  // Zero dashboards: show empty state and do not render children that require a valid dashboard id
  if (!currentDashboardId) {
    return (
      <LogInRequired>
        <div className="container mx-auto max-w-7xl px-6 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <EmptyStateCTA
              title="Create your first Dashboard"
              description="Dashboards bring your evaluation results together in one place."
            >
              <NewResourceButton
                resourceName="Default Dashboard"
                data-testid="dashboard-create-default-button"
                onClick={() => {
                  void handleCreateDashboard("Default Dashboard")
                }}
              />
            </EmptyStateCTA>
          </div>
        </div>
      </LogInRequired>
    )
  }

  const isFocusedMode = !!unifiedMetricDrillDown
  const focusedWidget = focusedWidgetForBreadcrumb

  return (
    <LogInRequired>
      {/* Declares the dashboard this page shows. Read by
          app-covi/utils/viewContext — data-only, no module import. */}
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        data-testid="dashboard-page"
        data-covi-view={
          currentDashboardId
            ? JSON.stringify({
                kind: "dashboard",
                id: currentDashboardId,
                name: currentDashboard?.display_name,
              })
            : undefined
        }
      >
        {/* Static Header - not in scroll container so it never moves */}
        {/* Hide entirely when in unified drill-down mode (no content to show) */}
        {!isFocusedMode && (
          <div className="z-10 min-w-0 shrink-0">
            <div className="flex min-w-0 items-center justify-between px-6 pb-4">
              <div className="flex min-w-0 items-center gap-2">
                {/* Calendar button + Dashboard Actions (currentDashboardId is defined here) */}
                {!isFocusedMode && (
                  <>
                    <CalendarDateRangeButton
                      currentDashboardId={currentDashboardId}
                    />
                    <DashboardActions
                      dashboardId={currentDashboardId}
                      dashboardUrl={queryParams.getHref(
                        `/dashboard/${currentDashboardId}`,
                        organizationSlug,
                      )}
                      attribution={currentDashboard?.attribution}
                      onClearDashboard={handleClearDashboard}
                      onDeleteDashboard={() =>
                        handleDeleteDashboard(currentDashboardId)
                      }
                      onExportDashboard={handleExportDashboard}
                      onImportDashboard={handleImportDashboard}
                      widgetCount={widgets.length}
                      isDefaultDashboard={currentDashboard?.is_default ?? false}
                      dashboardName={
                        currentDashboard?.display_name ?? "Dashboard"
                      }
                    />
                    {isSavingDashboard && (
                      <span
                        className="text-xs text-coval-text-muted"
                        aria-live="polite"
                      >
                        Saving changes...
                      </span>
                    )}
                    {/* <DashboardFiltersDropdown
                    organizationSlug={organizationSlug}
                    currentDashboardId={currentDashboardId}
                  /> */}
                  </>
                )}

                {/* Widget Title removed from header — now inside chart widget */}
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content - only this area scrolls; gray bg fills viewport */}
        <div
          className={`min-h-0 min-w-0 flex-1 transition-all duration-500 ease-in-out ${isFocusedMode ? "overflow-x-auto overflow-y-hidden" : "overflow-x-hidden overflow-y-auto"} bg-surface-contrast`}
        >
          {widgets.length === 0 ? (
            <div className="px-8 pb-6 pt-6">
              <EmptyDashboardState onAddWidget={handleAddWidgetClick} />
            </div>
          ) : isFocusedMode && focusedWidget ? (
            /* Focused Layout - 50/50 flexbox design */
            <div className="flex h-full min-w-0 flex-row gap-6 pl-6 animate-in fade-in duration-500">
              {/* Chart Section - 50% width */}
              <div className="flex min-h-0 min-w-[400px] flex-1 flex-col gap-4 overflow-hidden py-3 pr-1">
                <Card className="h-[350px] shrink-0">
                  <div className="flex h-full flex-col p-6">
                    <h3 className="text-sm font-semibold text-coval-text truncate mb-2 shrink-0">
                      {focusedWidget.config?.title ||
                        focusedWidget.title ||
                        focusedWidget.type}
                    </h3>
                    <div className="flex-1 min-h-0">
                      <WidgetFillerContent
                        type={focusedWidget.type}
                        widget={focusedWidget}
                        onChartClick={handleWidgetChartClick}
                        onPointClick={handleWidgetPointClick}
                        onCellClick={handleTableCellClick}
                      />
                    </div>
                  </div>
                </Card>
                <div className="min-h-0 flex-1 pb-3">
                  {unifiedMetricDrillDown ? (
                    <DashboardDrilldownInsightsPanel
                      chart={dashboardInsightChart}
                      rows={dashboardInsightRows}
                      isRowsLoading={unifiedDrillDownRowsState.isLoading}
                      rowsError={unifiedDrillDownRowsState.error}
                      onEvidenceLinkClick={handleInsightEvidenceLinkClick}
                      onEvidenceLinkHover={handleInsightEvidenceLinkHover}
                    />
                  ) : null}
                </div>
              </div>

              {/* Run Details Panel - 50% width */}
              <div className="flex-1 min-w-[300px] h-full">
                <div className="h-full">
                  {unifiedMetricDrillDown ? (
                    <UnifiedMetricDrillDownPanel
                      clickData={unifiedMetricDrillDown.clickData}
                      baselineId={focusedWidget?.config?.baselineId}
                      drillDownRowsState={unifiedDrillDownRowsState}
                      focusedSimulationOutputId={
                        focusedInsightSimulationOutputId
                      }
                      highlightedSimulationOutputId={
                        highlightedInsightSimulationOutputId
                      }
                      onClose={closeUnifiedMetricDrillDown}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            /* Regular Grid Layout */
            <GridDashboardLayout
              widgets={widgets}
              dashboardId={currentDashboardId}
              onRemove={handleRemoveWidget}
              onConfigure={handleConfigureWidget}
              onClone={(widget) => {
                handleCloneWidget(widget)
              }}
              onConfigChange={(widgetId, config) => {
                void handleUpdateWidget(widgetId, config)
              }}
              onMoveWidget={handleWidgetMove}
              onWidthChange={handleWidgetWidthChange}
              onChartClick={handleWidgetChartClick}
              onPointClick={handleWidgetPointClick}
              onCellClick={handleTableCellClick}
            />
          )}
        </div>
      </div>
      <DashboardTableCellDrillDownDrawer
        selection={tableCellDrillDown}
        onClose={closeTableCellDrillDown}
      />
    </LogInRequired>
  )
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  dashboardId,
}) => {
  return (
    <DashboardFiltersProvider>
      <DashboardContent key={dashboardId} dashboardId={dashboardId} />
    </DashboardFiltersProvider>
  )
}
